import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Delete, FileText, User, Wallet, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { money2, roundOffToUnit } from "@/lib/format";
import { useCurrency } from "@/hooks/useCurrency";

function Key({ label, onClick, variant = "num", testid, className, disabled }) {
  const styles = {
    num: "bg-white border-[#D7C4B6] text-brand-brown hover:bg-[#FFF3E8] text-[20px] font-bold",
    muted: "bg-[#F4EBE3] border-[#D7C4B6] text-brand-brown hover:bg-[#EDE0D4] text-[12px] font-bold",
    red: "bg-red-50 border-red-300 text-red-700 hover:bg-red-100 text-[13px] font-bold",
    green: "bg-brand-green border-brand-green text-white hover:bg-[#1e9a52] text-[14px] font-black",
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "h-12 min-h-[44px] rounded-lg border transition-colors active:translate-y-px flex items-center justify-center touch-manipulation disabled:opacity-50",
        styles[variant],
        className,
      )}
    >
      {label}
    </button>
  );
}

export default function PosCashReceiptPopup({
  open,
  due,
  method,
  onMethodChange,
  trn,
  onTrnChange,
  customerName,
  onCustomerNameChange,
  onPickCustomer,
  tendered,
  onDigit,
  onBackspace,
  onClear,
  onConfirm,
  onClose,
  pending,
  keysPaused = false,
  roundOff = false,
  onRoundOffChange,
}) {
  const { t } = useTranslation();
  const { code: currencyCode, formatPlain, roundOff: calcRoundOff } = useCurrency();
  const amountRef = useRef(null);
  const trnRef = useRef(null);
  const [taxOpen, setTaxOpen] = useState(false);

  const baseDue = Number(due) || 0;
  const roundDelta = calcRoundOff(baseDue);
  const showRoundOff = Math.abs(roundDelta) >= 0.0005;
  const dueAmt = showRoundOff && roundOff ? Number(formatPlain(baseDue + roundDelta)) : baseDue;
  const tenderedAmt = Number(tendered);
  const usesKeypad = method === "cash" || method === "split";
  const cardAmt = Number(formatPlain(dueAmt - (Number.isNaN(tenderedAmt) ? 0 : tenderedAmt)));
  const cashOk = method === "cash" && !Number.isNaN(tenderedAmt) && tenderedAmt + 0.001 >= dueAmt;
  const splitOk = method === "split"
    && !Number.isNaN(tenderedAmt)
    && tenderedAmt >= 0.01
    && tenderedAmt + 0.01 < dueAmt;
  const canConfirm = !pending && (method === "card" || cashOk || splitOk);
  const change = cashOk ? tenderedAmt - dueAmt : 0;

  const trnValue = String(trn || "").trim();
  const nameValue = String(customerName || "").trim();
  const hasTaxDetails = Boolean(trnValue || (nameValue && nameValue !== "Walk-in"));
  const taxIdLabel = currencyCode === "OMR" ? "VATIN" : "TRN";
  const taxSummary = [
    trnValue ? `${taxIdLabel} ${trnValue}` : null,
    nameValue && nameValue !== "Walk-in" ? nameValue : null,
  ].filter(Boolean).join(" · ");

  useEffect(() => {
    if (!open) {
      setTaxOpen(false);
      return undefined;
    }
    const trnFilled = String(trn || "").trim();
    const nameFilled = String(customerName || "").trim();
    setTaxOpen(Boolean(trnFilled || (nameFilled && nameFilled !== "Walk-in")));
    const timer = window.setTimeout(() => amountRef.current?.focus(), 40);
    return () => window.clearTimeout(timer);
    // Only when dialog opens — don't fight the user while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    function onKey(e) {
      if (keysPaused) return;
      const tag = e.target?.tagName;
      const typingTrnOrName = tag === "INPUT" && e.target?.dataset?.cashField !== "amount";
      if (e.key === "Escape") {
        e.preventDefault();
        return;
      }
      if (e.key === "F2") {
        e.preventDefault();
        setTaxOpen(true);
        onPickCustomer();
        return;
      }
      if (!usesKeypad || typingTrnOrName) {
        if (e.key === "Enter" && method === "card") {
          e.preventDefault();
          if (!pending && canConfirm) onConfirm();
        }
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (pending || !canConfirm) return;
        onConfirm();
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        onBackspace();
        return;
      }
      if (e.key === "Delete") {
        e.preventDefault();
        onClear();
        return;
      }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        onDigit(e.key);
      } else if (e.key === ".") {
        e.preventDefault();
        onDigit(".");
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, method, usesKeypad, pending, canConfirm, keysPaused, onBackspace, onClear, onClose, onConfirm, onDigit, onPickCustomer]);

  const handleTrnChange = (value) => {
    onTrnChange(String(value || "").replace(/\D/g, "").slice(0, 15));
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/30 backdrop-blur-[2px] p-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pos-cash-receipt-title"
      data-testid="pos-cash-receipt-dialog"
    >
      <div className="w-full max-w-[820px] min-w-0 bg-[#EFE6DE] rounded-xl border border-[#D7C4B6] shadow-2xl overflow-hidden">
        <div className="px-3 py-2 flex items-center justify-between bg-[#F7EFE8] border-b border-[#E0D0C4]">
          <h2 id="pos-cash-receipt-title" className="text-[14px] font-bold text-brand-brown flex items-center gap-2">
            <Wallet size={15} className="text-brand-orange" />
            {t("pos.cashReceipt")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="h-8 w-8 rounded-lg bg-white border border-[#E0D0C4] grid place-items-center text-brand-brown hover:bg-[#FFF3E8]"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1.5 px-3 pt-2">
          {[
            { id: "cash", label: t("pos.cash") },
            { id: "card", label: t("pos.card") },
            { id: "split", label: t("pos.splitPay") },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onMethodChange(m.id)}
              className={cn(
                "h-9 rounded-lg border-2 text-[11px] font-black touch-manipulation",
                method === m.id
                  ? m.id === "cash"
                    ? "border-brand-green bg-brand-green text-white"
                    : m.id === "split"
                      ? "border-brand-orange bg-brand-orange text-white"
                      : "border-brand-orange bg-[#FFF3E8] text-brand-brown"
                  : "border-[#E0D0C4] bg-white text-brand-brown hover:bg-[#FFF3E8]",
              )}
              data-testid={`pos-pay-method-${m.id}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="p-3 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(280px,320px)] gap-3 min-w-0 items-start">
          <div className="min-w-0 rounded-xl border border-[#D7C4B6] bg-white p-3 space-y-2.5 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-2 rounded-lg bg-[#FFF3E8] border border-[#F0E0D4] px-3 py-2">
              <div>
                <div className="text-[9px] uppercase tracking-widest font-bold text-brand-brown/50">
                  {t("pos.amountDue", "Amount due")}
                </div>
                <div className="text-[10px] font-bold text-brand-brown/40">{currencyCode}</div>
              </div>
              <div className="font-mono font-black text-[26px] leading-none tabular-nums text-brand-orange" data-testid="pos-pay-due">
                {money2(dueAmt)}
              </div>
            </div>

            {showRoundOff && (
              <label className="flex items-center justify-between gap-2 rounded-lg border border-[#F0E0D4] bg-[#FFFCFA] px-3 py-2 cursor-pointer" data-testid="pos-round-off">
                <span className="flex items-center gap-2 text-[12px] font-bold text-brand-brown">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-orange"
                    checked={roundOff}
                    onChange={(e) => onRoundOffChange?.(e.target.checked)}
                  />
                  {t("pos.roundOff")}
                </span>
                {roundOff && (
                  <span className="font-mono tabular-nums text-[12px] font-black text-brand-orange">
                    {roundDelta > 0 ? "+" : ""}{money2(roundDelta)}
                  </span>
                )}
              </label>
            )}

            {usesKeypad ? (
              <label className="block space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-brand-brown/55">
                  {method === "split" ? t("pos.cashAmount") : t("pos.receiptAmount")}
                </span>
                <input
                  ref={amountRef}
                  value={tendered}
                  readOnly
                  data-cash-field="amount"
                  data-testid="pos-tendered"
                  className="w-full h-12 rounded-lg border-2 border-brand-orange bg-[#E8F1FF] px-2.5 font-mono text-[22px] font-black text-brand-brown tabular-nums text-right focus:outline-none"
                />
              </label>
            ) : (
              <p className="text-[12px] text-brand-brown/60 font-medium px-0.5">
                {t("pos.cardPayHint", "Card is charged for the amount due. No tendered cash.")}
              </p>
            )}

            {method === "split" && splitOk && (
              <div className="rounded-lg border border-[#F0E0D4] bg-[#FFFCFA] px-2.5 py-1.5 space-y-0.5 text-[12px] font-bold" data-testid="pos-split-preview">
                <div className="flex justify-between gap-2">
                  <span className="text-brand-brown/55">{t("pos.cash")}</span>
                  <span className="font-mono tabular-nums">{money2(tenderedAmt)}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-brand-brown/55">{t("pos.card")}</span>
                  <span className="font-mono tabular-nums">{money2(cardAmt)}</span>
                </div>
              </div>
            )}
            {method === "split" && tendered !== "" && !splitOk && (
              <p className="text-[12px] text-red-600 font-semibold">
                {t("pos.splitHint", "Enter a cash amount less than the bill total")}
              </p>
            )}

            {method === "cash" && tendered !== "" && Number(tendered) + 0.001 < dueAmt && (
              <p className="text-[12px] text-red-600 font-semibold">{t("pos.tenderedShort", "Tendered is less than amount due")}</p>
            )}
            {method === "cash" && change > 0.001 && (
              <div className="flex items-center justify-between text-[13px] font-bold text-emerald-700" data-testid="pos-pay-change">
                <span>{t("pos.changeDue")}</span>
                <span className="font-mono tabular-nums">{money2(change)}</span>
              </div>
            )}

            <div className="rounded-lg border border-[#E0D0C4] bg-[#FFFCFA] overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  setTaxOpen((v) => {
                    const next = !v;
                    if (next) {
                      window.setTimeout(() => trnRef.current?.focus(), 40);
                    } else {
                      window.setTimeout(() => amountRef.current?.focus(), 40);
                    }
                    return next;
                  });
                }}
                className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-[#FFF3E8] touch-manipulation"
                data-testid="pos-tax-toggle"
                aria-expanded={taxOpen}
              >
                <FileText size={14} className="text-brand-orange shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-bold text-brand-brown">
                    {t("pos.taxInvoiceDetails", "TRN / company (optional)")}
                  </div>
                  {!taxOpen && (
                    <div className="text-[10px] text-brand-brown/55 truncate">
                      {taxSummary || t("pos.taxInvoiceHint", "Tap to add tax invoice details")}
                    </div>
                  )}
                </div>
                <ChevronDown
                  size={15}
                  className={cn("text-brand-brown/50 shrink-0 transition-transform", taxOpen && "rotate-180")}
                />
              </button>

              {taxOpen && (
                <div className="px-2.5 pb-2.5 space-y-2 border-t border-[#E8DDD4]">
                  <label className="block space-y-1 pt-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand-brown/55">
                      {taxIdLabel}
                    </span>
                    <input
                      ref={trnRef}
                      value={trn}
                      onChange={(e) => handleTrnChange(e.target.value)}
                      placeholder="100xxxxxxxx00003"
                      maxLength={15}
                      inputMode="numeric"
                      className="w-full h-10 rounded-lg border border-[#E0D0C4] bg-white px-2.5 font-mono text-[13px] text-brand-brown focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                      data-testid="pos-cash-trn"
                      data-cash-field="trn"
                      autoComplete="off"
                    />
                  </label>

                  <div className="space-y-1 min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand-brown/55">
                      {t("pos.companyName", "Company name")}
                    </span>
                    <div className="flex items-stretch gap-1.5 min-w-0 w-full">
                      <input
                        value={customerName}
                        onChange={(e) => onCustomerNameChange(e.target.value)}
                        placeholder={t("pos.companyNamePlaceholder", "Company / customer on receipt")}
                        className="min-w-0 flex-1 h-10 rounded-lg border border-[#E0D0C4] bg-white px-2.5 text-[13px] font-semibold text-brand-brown focus:outline-none focus:ring-2 focus:ring-brand-orange/30"
                        data-testid="pos-cash-customer-name"
                        data-cash-field="name"
                      />
                      <button
                        type="button"
                        onClick={onPickCustomer}
                        className="h-10 px-2 rounded-lg border border-brand-orange/40 bg-[#FFF3E8] text-brand-orange text-[10px] font-bold inline-flex items-center justify-center gap-1 shrink-0 hover:bg-[#FFE8D4] touch-manipulation"
                        data-testid="pos-cash-party-name"
                        title={`${t("pos.partyName")} (F2)`}
                      >
                        <User size={13} className="shrink-0" />
                        <span className="hidden sm:inline truncate">{t("pos.partyName")}</span>
                        <span className="text-[9px] font-mono opacity-70 shrink-0">F2</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {usesKeypad ? (
            <div className="rounded-xl border border-[#D7C4B6] bg-[#F7EFE8] p-2.5 grid grid-cols-4 gap-2 self-start w-full">
              <Key label="7" onClick={() => onDigit("7")} testid="cash-key-7" />
              <Key label="8" onClick={() => onDigit("8")} testid="cash-key-8" />
              <Key label="9" onClick={() => onDigit("9")} testid="cash-key-9" />
              <Key label="C" onClick={onClear} variant="red" testid="cash-key-C" />
              <Key label="4" onClick={() => onDigit("4")} testid="cash-key-4" />
              <Key label="5" onClick={() => onDigit("5")} testid="cash-key-5" />
              <Key label="6" onClick={() => onDigit("6")} testid="cash-key-6" />
              <Key label={<Delete size={16} />} onClick={onBackspace} variant="muted" testid="cash-key-bs" />
              <Key label="1" onClick={() => onDigit("1")} testid="cash-key-1" />
              <Key label="2" onClick={() => onDigit("2")} testid="cash-key-2" />
              <Key label="3" onClick={() => onDigit("3")} testid="cash-key-3" />
              <Key label="Esc" onClick={onClose} variant="muted" testid="cash-key-esc" />
              <Key label="0" onClick={() => onDigit("0")} testid="cash-key-0" />
              <Key label="00" onClick={() => onDigit("00")} testid="cash-key-00" />
              <Key label="." onClick={() => onDigit(".")} testid="cash-key-dot" />
              <Key
                label="Enter"
                onClick={onConfirm}
                variant="green"
                testid="pos-pay-confirm"
                disabled={!canConfirm}
                className="text-[13px]"
              />
            </div>
          ) : (
            <div className="rounded-xl border border-[#D7C4B6] bg-[#F7EFE8] p-3 flex flex-col gap-2 self-start">
              <button
                type="button"
                onClick={onClose}
                disabled={pending}
                className="h-11 rounded-lg border border-[#D7C4B6] bg-white text-brand-brown font-bold text-[13px]"
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={pending}
                data-testid="pos-pay-confirm"
                className="h-12 rounded-lg bg-brand-green text-white font-black text-[14px] hover:bg-[#1e9a52] disabled:opacity-60"
              >
                {t("pos.confirmPayment", "Confirm payment")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
